import { useQuery } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { InfoRow } from 'common/components/InfoRow';
import { formatDate } from 'common/utils/formatDate';
import { IFeature, UserProfileExtendedDocument } from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { LastActiveLabel } from 'user/components/LastActiveLabel';

interface IProps {
    userId: string;
}

export const UserExtendedInfoRows = ({ userId }: IProps) => {
    const [translate] = useTranslation();
    const { data } = useQuery(UserProfileExtendedDocument, {
        variables: { id: userId },
    });

    const { canUse: hasUserActivity } = useFeature(IFeature.UserActivity);
    const { user } = data || {};

    if (!user) return null;

    const { invitedOn, lastLogin, lastActivity, activityCount, userVia } = user;
    const lastActiveDate = hasUserActivity ? lastActivity?.endedAt : lastLogin;

    if (!invitedOn && !lastActiveDate && !userVia?.length) return null;

    return (
        <>
            {!!invitedOn && (
                <InfoRow description={translate('invitedOn')}>
                    {formatDate(invitedOn, 'd LLL yyyy HH:mm')}
                </InfoRow>
            )}

            {!!lastActiveDate && (
                <InfoRow
                    description={translate(
                        hasUserActivity ? 'lastActive' : 'lastLogin'
                    )}
                >
                    <LastActiveLabel
                        showDuration
                        showTooltip
                        activity={
                            hasUserActivity
                                ? lastActivity
                                : { endedAt: lastLogin }
                        }
                    />
                </InfoRow>
            )}

            {hasUserActivity && (
                <InfoRow
                    description={
                        <>
                            {translate('activityCount')}
                            <Typography
                                color="text.secondary"
                                variant="caption"
                            >
                                {translate('activityCountSubtitle')}
                            </Typography>
                        </>
                    }
                    descriptionSx={{ display: 'flex', flexDirection: 'column' }}
                >
                    {translate('sessionsCount', { count: activityCount ?? 0 })}
                </InfoRow>
            )}

            {!!userVia?.length && (
                <InfoRow description={translate('via')}>
                    {userVia.join(', ')}
                </InfoRow>
            )}
        </>
    );
};
